import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { useConfirm, useDeleteFlow } from '../../components/confirm';
import { fuzzyMatch } from '../../utils/fuzzyMatch';
import { ProjectNotesButton } from '../../components/ProjectNotes';
import '../backlog/backlog.css';
import PreLoginSettingsDetailPage, { PRE_LOGIN_FORM, type PostSaveTarget } from './preLoginSettings/PreLoginSettingsDetailPage';
import PreLoginNewJobDialog, { type NewJobCreated } from './preLoginSettings/PreLoginNewJobDialog';
import type { PreLoginListRow } from './preLoginSettings/types';

/**
 * Settings → Pre-Login Settings — LIST view (the landing view, standing rule 10).
 *
 * Port of the legacy XAF `COCSettings_ListView`. Its EIGHT columns, in order
 * (sorted descending by COC id):
 *
 *   COCID · ProjectID · ProjectName · ProjectLocation · ProjectCode ·
 *   #DaysinAdvance · Client · ClientAddress
 *
 * Taken from the LIVE GoLive grid, which disagrees with the v26.9.2
 * `BTLIMS.Web/Model.xafml` node at line 6172 (that one shows a Division column,
 * marks `DaysinAdvance` `Removed="True"`, and lists neither ProjectLocation nor
 * ClientAddress). GoLive wins — the same call already recorded for Workflow
 * Status Chain. Nothing else is shown: this grid is not a place to add
 * "useful" extras.
 *
 * The legacy nav also has a second entry, "Retired Pre-Login"
 * (`COCSettings_ListView_RetriveBy`), which is the same list with the retired
 * rows shown. That is the `Retired` chip here rather than a separate screen —
 * one form, one grid, one toolbar (standing rule 9).
 */
export default function PreLoginSettingsPage() {
  const notify = useNotify();
  const runDelete = useDeleteFlow();
  const { confirm } = useConfirm();

  const [rows, setRows]       = useState<PreLoginListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const [search, setSearch]   = useState('');
  const [scope, setScope]     = useState<'active' | 'retired'>('active');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  /** The "New Job" dialog's selection, frozen when it opens (legacy
   *  COCSettingsController.NewJobRoute_Excute reads View.SelectedObjects once). */
  const [newJobFor, setNewJobFor] = useState<string[] | null>(null);

  /** null = list; { oid: null } = New; { oid } = edit that record. */
  // `openAfter` is set only by the detail page's "save, then open X" path: that
  // save mints the oid, which changes the detail page's key and remounts it, so
  // the intent cannot live in its own state. It round-trips through here.
  const [detail, setDetail] =
    useState<{ oid: string | null; openAfter?: PostSaveTarget } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const started = performance.now();
    try {
      const res = await api.get('/api/v1/pre-login-settings', {
        params: { includeRetired: scope === 'retired' },
      });
      setRows(res.data?.data ?? []);
    } catch {
      setError('Could not load Pre-Login Settings.');
      notify.error('Could not load Pre-Login Settings.');
    } finally {
      setLoadMs(performance.now() - started);
      setLoading(false);
    }
  }, [scope, notify]);

  useEffect(() => { void load(); }, [load]);

  // Client-side search over what the grid shows — the server filter exists too
  // (`search` param) but a typed filter should not round-trip on every keystroke.
  // Uses the SHARED fuzzyMatch (BO UI Standard v4 §9 / the Class A-B checklist:
  // "fuzzy search box in its own row above the grid, left-aligned, shared
  // fuzzyMatch") — never a bespoke `.includes()`.
  const filtered = useMemo(() => {
    const q = search.trim();
    if (!q) return rows;
    // Searches exactly the text columns the grid shows, so a hit is always
    // visible in the result.
    return rows.filter(r =>
      [r.cocId, r.projectRef, r.projectName, r.projectLocation,
       r.projectCode, r.client, r.clientAddress]
        .some(v => fuzzyMatch(q, v ?? '')));
  }, [rows, search]);

  const selectedRow = useMemo(
    () => (selected.size === 1 ? rows.find(r => r.oid === [...selected][0]) ?? null : null),
    [selected, rows]);

  /** The GoLive grid's EIGHT columns, in its order — nothing else (owner
   *  screenshot 2026-09-23).
   *
   *  This deliberately DISAGREES with the v26.9.2 `Model.xafml` Columns node,
   *  which lists a Division column, marks `DaysinAdvance` `Removed="True"` and
   *  never mentions ProjectLocation or ClientAddress. The live grid is
   *  authoritative — the same call already recorded for Workflow Status Chain
   *  (CLAUDE.md §14b.2). An earlier pass rendered the xafml set plus seven
   *  "useful" extras (COC Name, Sample Matrix, TAT, Samples, Retire Date, Last
   *  Updated, Updated By); all are gone, and the SP no longer fetches them. */
  const columns: AppColumn<PreLoginListRow>[] = useMemo(() => [
    { field: 'cocId',           header: 'COCID',          width: 100 },
    { field: 'projectRef',      header: 'ProjectID',      width: 160 },
    { field: 'projectName',     header: 'ProjectName',    width: 160 },
    { field: 'projectLocation', header: 'ProjectLocation', width: 130 },
    { field: 'projectCode',     header: 'ProjectCode',    width: 130 },
    { field: 'daysInAdvance',   header: '#DaysinAdvance', width: 110, align: 'right' },
    { field: 'client',          header: 'Client',         width: 140 },
    { field: 'clientAddress',   header: 'ClientAddress',  width: 140 },
  ], []);

  const openNew  = useCallback(() => setDetail({ oid: null }), []);
  const openEdit = useCallback((row: PreLoginListRow) => setDetail({ oid: row.oid }), []);

  const doDelete = useCallback(async () => {
    if (selected.size === 0) return;
    const ids = [...selected];

    // Legacy DeleteAction_Executing cancels the ENTIRE delete when ANY selected
    // COC is already imported into a job. Refusing up front matters here: the
    // loop below deletes sequentially, so without this the rows before a blocked
    // one would already be gone when the API refused.
    const blocked = ids
      .map(id => rows.find(r => r.oid === id))
      .filter((r): r is PreLoginListRow => !!r?.isImported);
    if (blocked.length > 0) {
      notify.error('Cannot delete the COCID because it is already imported. ');
      return;
    }
    const label = ids.length === 1
      ? (rows.find(r => r.oid === ids[0])?.cocId ?? 'this record')
      : `${ids.length} records`;

    await runDelete({
      formName: PRE_LOGIN_FORM,
      message: `Delete ${label}? Its samples, tests and bottle allocations are removed with it.`,
      delete: async () => {
        // Sequential on purpose: each delete is its own audited transaction, and
        // a multi-select delete here is a handful of rows, not a bulk operation.
        for (const id of ids) await api.delete(`/api/v1/pre-login-settings/${id}`);
      },
      onDeleted: () => { setSelected(new Set()); void load(); },
    });
  }, [selected, rows, runDelete, load]);

  /**
   * "Save As" — legacy COCSettingsController.COCSaveAs_Execute.
   *
   * Legacy requires EXACTLY one selected row: >1 shows "selectonlychk",
   * 0 shows "selectchkbox".
   *
   * The HEADER clone is thin — eleven fields, no client and no project — but the
   * SAMPLES, their tests and their bottle allocations DO come across: legacy
   * copies them on the clone's first save (COCSettingsViewController.SaveAs()),
   * and the SP does it in the same transaction. The user lands on the new record
   * to fill in the client/project, which is why legacy opens the clone in the
   * detail view rather than returning to the list.
   */
  const doSaveAs = useCallback(async () => {
    if (selected.size === 0) { notify.warning('Select the checkbox.'); return; }
    if (selected.size > 1)   { notify.warning('Select only one checkbox.'); return; }

    const id  = [...selected][0];
    const src = rows.find(r => r.oid === id);
    const ok  = await confirm({
      title: 'Save As',
      message: `Create a copy of ${src?.cocId ?? 'this record'}? `
             + 'The copy takes its samples, tests, containers, attachments and '
             + 'notes — you fill in the client and project on the copy.',
      confirmLabel: 'Save As',
    });
    if (!ok) return;

    try {
      const res = await api.post(`/api/v1/pre-login-settings/${id}/save-as`);
      const clone = res.data?.data as { oid: string; cocId: string | null } | undefined;
      notify.success('Saved successfully.');   // the one shared wording (§18.6)
      setSelected(new Set());
      await load();
      if (clone?.oid) setDetail({ oid: clone.oid });   // legacy opens the clone
    } catch (err) {
      // Legacy shows the exception's own message; a bare "could not copy" hides
      // the one thing that says why (e.g. the COC-ID lock timing out).
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      notify.error(msg || 'Could not copy this Pre-Login Setting.');
    }
  }, [selected, rows, confirm, notify, load]);

  /**
   * "Reactivate" — legacy COCSettingsController.Active_Execute on the Retired
   * list: clears Retire + RetireDate on every selected row, then
   * "Reactivated successfully." With nothing selected legacy says
   * "Please select the COCID."
   */
  const doReactivate = useCallback(async () => {
    if (selected.size === 0) { notify.error('Please select the COCID.'); return; }
    const ids = [...selected];
    const ok  = await confirm({
      title: 'Reactivate',
      message: ids.length === 1
        ? `Reactivate ${rows.find(r => r.oid === ids[0])?.cocId ?? 'this record'}?`
        : `Reactivate ${ids.length} records?`,
      confirmLabel: 'Reactivate',
    });
    if (!ok) return;

    try {
      // Sequential on purpose: legacy loops the selection and each record is its
      // own audited change, so the trail reads one row per COC.
      for (const id of ids) await api.post(`/api/v1/pre-login-settings/${id}/reactivate`);
      notify.success('Reactivated successfully.');
      setSelected(new Set());
      await load();
    } catch {
      notify.error('Could not reactivate.');
    }
  }, [selected, rows, confirm, notify, load]);

  /**
   * "New Job" — legacy `COCSettingsController.NewJobRoute_Excute`.
   *
   * Legacy needs at least one ticked row ("selectchkbox") and refuses the whole
   * batch when ANY of them carries no route ID. The route check is the SERVER's
   * (the preview returns which COCs have none), so this only guards the
   * selection; re-deriving the route rule here would be a second copy of it.
   */
  const doNewJob = useCallback(() => {
    if (selected.size === 0) { notify.error('Select the checkbox.'); return; }
    setNewJobFor([...selected].map(String));
  }, [selected, notify]);

  const onJobsCreated = useCallback((jobs: NewJobCreated[]) => {
    setNewJobFor(null);
    setSelected(new Set());
    // Legacy: "Job ID was created successfully." — with the ids, because the
    // user's next step is to go and find them in Sample Registration.
    notify.success(jobs.length === 1
      ? `Job ID ${jobs[0]!.jobID} was created successfully.`
      : `${jobs.length} Job IDs were created successfully: ${jobs.map(j => j.jobID).join(', ')}`);
    void load();
  }, [notify, load]);

  // ── Detail view REPLACES the list (standing rule 12) ───────────────────────
  // After every hook above, so hook order stays stable (rules of hooks).
  if (detail) {
    return (
      <PreLoginSettingsDetailPage
        key={detail.oid ?? 'new'}
        oid={detail.oid}
        siblingIds={filtered.map(r => r.oid)}
        initialTarget={detail.openAfter}
        onClose={() => setDetail(null)}
        onNavigate={(oid, openAfter) => setDetail({ oid, openAfter })}
        onSaved={() => void load()}
      />
    );
  }

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Settings</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Pre-Login Settings</span>
          </div>
          {/* BO UI Standard v4 §4: "Keep the title row clean — no descriptive
              sub-caption or helper text." The row carries ONLY the FormTitle
              (icon/colour/text from navMeta), the load-time chip and the
              view-only Audit chip; the grid and its footer count already say
              what a description would. */}
          <div className="dp-title-row">
            <FormTitle pageId="set-prelogin" auditFormName={PRE_LOGIN_FORM}
                       loadMs={loadMs} loading={loading} />
            {/* v4 §8: Project Notes is mandatory on EVERY screen of a BO —
                "List, Detail, AND New/Create screens all ship both components",
                and the one exemption is a screen with no single record to
                attach a note to. A list row IS a record, so the button hangs
                off the ticked one and tells the user to pick one when none is.
                The SHARED component (rule 8) over this form's existing note
                routes — never a second notes implementation. */}
            <ProjectNotesButton
              recordId={selectedRow?.oid}
              baseUrl={selectedRow ? `/api/v1/pre-login-settings/${selectedRow.oid}/notes` : null}
              recordLabel={selectedRow?.cocId ?? ''}
              recordSubLabel={selectedRow?.projectName ?? ''}
              businessObject={PRE_LOGIN_FORM}
              disabledReason="Tick one Pre-Login Setting to open its Project Notes"
            />
          </div>
        </div>

        {/* Active / Retired — the legacy nav's second "Retired Pre-Login" entry,
            folded into this one form as a scope chip (standing rule 9). */}
        <div className="sa-filter-row" style={{ marginTop: 4 }}>
          {(['active', 'retired'] as const).map(s => (
            <button key={s} className={`sa-filter-btn${scope === s ? ' active' : ''}`}
                    onClick={() => { setScope(s); setSelected(new Set()); }}>
              {s === 'active' ? 'Pre-Login Settings' : 'Retired Pre-Login'}
            </button>
          ))}
        </div>
      </div>

      <div className="bl-grid-area">
        {/* Toolbar order for THIS form (owner instruction 2026-10-12: "New, Edit,
            Delete should be first for this COC settings then Save As and New Job
            and then other button") — so:

              New · Edit · Delete · Save As · New Job [· Reactivate] · Refresh · Export to

            That is the shared `crudFirst` opt-in (standing rule 7), which moves
            the WHOLE New · Edit · Delete group — still in that order — ahead of
            the `left` slot and changes nothing else: same shared buttons, same
            style, same auto-registered Alt+N / Alt+E · F2 / Alt+D. It is a PROP
            on the shared component, never this form hand-rolling those three
            buttons into `left`. The canonical order (form-specific first) stays
            the default everywhere else.

            Audit, keyboard shortcuts and the load-time chip come free from the
            shared component — no per-form wiring. */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            crudFirst
            left={<>
              {/* After the CRUD group, per the owner order above. Reactivate only
                  exists on the Retired scope — legacy puts it on
                  COCSettings_ListView_RetriveBy and nowhere else. */}
              <button className="ca-btn" onClick={() => void doSaveAs()}
                      disabled={selected.size !== 1} title="Save As">
                <i className="ti ti-copy" /> Save As
              </button>
              {/* "New Job" — legacy NewJobRoute, targeted at COCSettings_ListView
                  only (never the detail). Enabled from one ticked row: it groups
                  the selection by route and creates a job per COC. */}
              <button className="ca-btn" onClick={doNewJob}
                      disabled={selected.size === 0} title="New Job">
                <i className="ti ti-briefcase" /> New Job
              </button>
              {scope === 'retired' && (
                <button className="ca-btn" onClick={() => void doReactivate()}
                        disabled={selected.size === 0} title="Reactivate">
                  <i className="ti ti-refresh-dot" /> Reactivate
                </button>
              )}
            </>}
            onNew={openNew}
            onEdit={() => selectedRow && openEdit(selectedRow)}
            editDisabled={!selectedRow}
            onDelete={() => void doDelete()}
            deleteDisabled={selected.size === 0}
            onRefresh={() => void load()}
            refreshing={loading}
            exportData={{
              filename: 'Pre-Login Settings',
              headers: columns.map(c => String(c.headerText ?? c.header)),
              rows: filtered.map(r => columns.map(c => String((r as never)[c.field] ?? ''))),
            }}
          />
        </div>

        <div className="list-search-row">
          <div className="list-search">
            <i className="ti ti-search" />
            <input
              type="text"
              placeholder="Search COC id, project, client…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          {filtered.length !== rows.length && (
            <span className="search-match-count">
              {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
            </span>
          )}
        </div>

        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err" style={{ margin: 12 }}>
              <i className="ti ti-alert-circle" /> {error}
              <button onClick={() => void load()} style={{ marginLeft: 10, color: 'var(--brand-accent, #e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
            </div>
          ) : (
            <AppDataGrid<PreLoginListRow>
              rows={filtered}
              columns={columns}
              getRowId={r => r.oid}
              loading={loading}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              onRowDoubleClick={openEdit}
              defaultPageSize={20}
              emptyText="No Pre-Login Settings."
            />
          )}
        </div>
      </div>

      {newJobFor && (
        <PreLoginNewJobDialog
          cocOids={newJobFor}
          onClose={() => setNewJobFor(null)}
          onCreated={onJobsCreated}
        />
      )}
    </div>
  );
}
